import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import { carName, carPhotoUrl } from "../components/CarCard";
import { SITE_URL, WHATSAPP_PRIMARY, waLink } from "../siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK = "/images/car-placeholder.webp";

function youtubeEmbed(url) {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube-nocookie.com/embed/${m[1]}` : null;
}

export default function CarDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const n = t("nx.cars");
  const [car, setCar] = useState(undefined);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    fetch(`${API}/cars/${id}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setCar).catch(() => setCar(null));
  }, [id]);

  if (car === undefined) return <div className="min-h-screen bg-[#050607]" />;
  if (car === null) {
    return (
      <div className="min-h-screen bg-[#050607] px-5 md:px-10 pt-40 pb-24" data-testid="car-not-found">
        <p className="mono text-[12px] text-[#666663] uppercase tracking-[0.14em] mb-8">{n.notFound}</p>
        <Link to="/cars" className="btn btn-solid justify-between w-fit"><span>{n.backToCars}</span><span aria-hidden="true">→</span></Link>
      </div>
    );
  }

  const photos = car.photo_ids || [];
  const embed = youtubeEmbed(car.video_url);
  const status = n.statusLabels[car.status] || car.status;
  const location = n.locationLabels[car.location] || car.location;
  const specs = [
    [n.f_make, car.make], [n.f_model, car.model], [n.f_year, car.year],
    [n.f_mileage, `${Number(car.mileage_km).toLocaleString()} ${n.km}`],
    [n.f_fuel, t("sell.fuelOpts")[car.fuel] || car.fuel],
    [n.f_transmission, t("sell.transOpts")[car.transmission] || car.transmission],
    [n.f_priceAed, `${Number(car.price_aed).toLocaleString()} AED`],
    [n.f_priceEur, `${Number(car.price_eur).toLocaleString()} EUR`],
    [n.f_location, location], [n.f_status, status],
  ];
  const msg = `${t("whatsapp.msg")} — ${carName(car)}`;

  return (
    <div className="bg-[#050607] min-h-screen" data-testid="car-detail-page">
      <Seo title={`${carName(car)} | VEYRONE`} description={`${carName(car)} — ${Number(car.mileage_km).toLocaleString()} km, ${location}. ${Number(car.price_eur).toLocaleString()} EUR · Full photo set and inspection summary.`} path={`/cars/${car.id}`} />
      <section className="px-5 md:px-10 pt-32 md:pt-40 pb-10">
        <Link to="/cars" className="mono text-[10px] tracking-[0.3em] text-[#666663] hover:text-[#F3F3F0] transition-colors uppercase" data-testid="car-back-link">← {n.backToCars}</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-6">
          <h1 className="font-display text-[32px] sm:text-[48px] text-[#F3F3F0]">{carName(car)}</h1>
          <span className="mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-[#D1C7BD]/50 text-[#D1C7BD] w-fit" data-testid="car-detail-status">{status}</span>
        </div>
        <p className="mono text-[11px] text-[#D1C7BD] mt-3">{Number(car.price_aed).toLocaleString()} AED · {Number(car.price_eur).toLocaleString()} EUR — {location}</p>
      </section>

      {/* GALLERY */}
      <section className="px-5 md:px-10 pb-16" data-testid="car-gallery">
        <div className="relative border border-white/10 bg-[#0A0B0C]">
          <img src={carPhotoUrl(car, idx)} alt={`${carName(car)} — photo ${idx + 1}`} width="1600" height="1000"
            loading={idx === 0 ? "eager" : "lazy"} onError={(e) => { if (!e.currentTarget.src.endsWith(FALLBACK)) e.currentTarget.src = FALLBACK; }}
            className="w-full aspect-[16/10] object-cover" data-testid="car-gallery-main" />
          {photos.length > 1 && (
            <>
              <button onClick={() => setIdx((idx - 1 + photos.length) % photos.length)} aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#050607]/80 border border-white/20 text-[#F3F3F0] cursor-pointer hover:border-white/50 transition-colors" data-testid="gallery-prev">←</button>
              <button onClick={() => setIdx((idx + 1) % photos.length)} aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#050607]/80 border border-white/20 text-[#F3F3F0] cursor-pointer hover:border-white/50 transition-colors" data-testid="gallery-next">→</button>
              <span className="absolute bottom-3 right-3 mono text-[10px] text-[#F3F3F0] bg-[#050607]/80 px-2.5 py-1.5" data-testid="gallery-counter">{idx + 1} / {photos.length}</span>
            </>
          )}
        </div>
        {photos.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2" data-testid="gallery-thumbs">
            {photos.map((pid, i) => (
              <button key={pid} onClick={() => setIdx(i)} aria-label={`Photo ${i + 1}`}
                className={`shrink-0 border transition-colors ${i === idx ? "border-[#D1C7BD]" : "border-white/10 hover:border-white/40"}`} data-testid={`gallery-thumb-${i}`}>
                <img src={`${API}/cars/${car.id}/photos/${pid}`} alt={`${carName(car)} thumbnail ${i + 1}`} width="160" height="100" loading="lazy" className="w-24 h-16 object-cover" />
              </button>
            ))}
          </div>
        )}
        {embed && (
          <div className="mt-6 border border-white/10" data-testid="car-video">
            <iframe src={`${embed}?rel=0`} title={`${carName(car)} — video`} className="w-full aspect-video" allowFullScreen loading="lazy" />
          </div>
        )}
      </section>

      {/* SPECS + INSPECTION */}
      <section className="px-5 md:px-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="eyebrow mb-8">{n.specsTitle}</div>
          <div className="border-t border-white/10">
            {specs.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-white/10 py-3.5" data-testid="car-spec-row">
                <span className="mono text-[10px] tracking-[0.16em] text-[#666663] uppercase">{k}</span>
                <span className="mono text-[12px] text-[#F3F3F0]">{v}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <a href={waLink(WHATSAPP_PRIMARY, msg)} target="_blank" rel="noopener noreferrer" className="btn justify-between" data-testid="car-detail-whatsapp">
              <span>{n.whatsBtn}</span><span aria-hidden="true">→</span>
            </a>
            <a href="#car-inquire" className="btn btn-solid justify-between" data-testid="car-detail-inquire">
              <span>{n.inquire}</span><span aria-hidden="true">↓</span>
            </a>
          </div>
        </div>
        <div data-testid="car-inspection">
          <div className="eyebrow mb-8">{n.inspectionTitle}</div>
          {car.inspection_summary
            ? <p className="text-[14px] leading-relaxed text-[#999995] whitespace-pre-line">{car.inspection_summary}</p>
            : <p className="mono text-[11px] text-[#666663] uppercase tracking-[0.14em]">{n.noInspection}</p>}
          <p className="text-[12px] text-[#666663] mt-6 leading-relaxed">{n.inspectionNote}</p>
        </div>
      </section>

      {/* INQUIRY */}
      <section id="car-inquire" className="hairline-t px-5 md:px-10 py-20 md:py-24">
        <div className="max-w-3xl">
          <div className="eyebrow mb-10">{n.inquireTitle} — {carName(car)}</div>
          <InquiryForm path="general" prefillMessage={`${t("whatsapp.msg")} — ${carName(car)}`} />
        </div>
      </section>
    </div>
  );
}
